import type { UserResource } from "@exonn/js-sdk";
import type { StorageKey } from "@exonn/storage";
import { setUserDataCache } from "src/user-data/cache/setUserDataCache.ts";
import { getUserDataState } from "src/user-data/getUserDataState.ts";
import { useUserDataApi } from "src/user-data/useUserDataApi.ts";
import { deepEq } from "@exonn/utils";
import { useForceRerender, useMemoizedFn } from "@exonn/hooks";
import type { ZodType } from "zod";
import { useEffect } from "react";

const subscribersMap = new Map<string, (() => void)[]>();

export function useUserDataState<V = any, D extends V = V>(options: {
  user: UserResource;
  key: StorageKey;
  validationSchema?: ZodType;
  sanitaze?: (data: V) => V;
  defaultValue: D;
  disableSavingToDatabase?: boolean;
}): {
  getValue: () => V;
  setValue: (value: V | null) => void;
  clear: () => void;
  value: V;
};
export function useUserDataState<
  V = any,
  D extends V | undefined = V | undefined,
>(options: {
  user: UserResource;
  key: StorageKey;
  validationSchema?: ZodType;
  sanitaze?: (data: V) => V;
  defaultValue?: D;
  disableSavingToDatabase?: boolean;
}): {
  getValue: () => V | undefined;
  setValue: (value: V | null) => void;
  clear: () => void;
  value: V | undefined;
};
export function useUserDataState<
  V = any,
  D extends V | undefined = V | undefined,
>(options: {
  user: UserResource;
  key: StorageKey;
  validationSchema?: ZodType;
  sanitaze?: (data: V) => V;
  defaultValue?: D;
  disableSavingToDatabase?: boolean;
}): {
  getValue: () => V | undefined;
  setValue: (value: V | null) => void;
  clear: () => void;
  value: V | undefined;
} {
  const {
    key,
    user,
    defaultValue,
    sanitaze,
    disableSavingToDatabase,
    validationSchema,
  } = options;
  const { setUserData } = useUserDataApi();
  const rerender = useForceRerender();
  const value = getUserDataState<V>({
    key,
    user,
    validationSchema,
    defaultValue,
    sanitaze,
  });

  const getValue = useMemoizedFn(() => {
    return getUserDataState<V>({
      user,
      key,
      defaultValue,
      validationSchema,
      sanitaze,
    });
  });

  const setValue = useMemoizedFn((value: V | null) => {
    const prevState = getValue();
    if (!deepEq(prevState, value)) {
      setUserDataCache<V>({
        user,
        key,
        value,
      });
      const subscribers = subscribersMap.get(JSON.stringify(key)) || [];

      subscribers.forEach((cb) => {
        cb();
      });
      if (!disableSavingToDatabase) {
        setUserData<V>({
          key: key,
          value: value,
        });
      }
    }
  });

  if (!subscribersMap.has(JSON.stringify(key))) {
    subscribersMap.set(JSON.stringify(key), []);
  }

  useEffect(() => {
    // todo: useSyncExternalStore ?
    const callback = () => {
      rerender();
    };
    const subscribers = subscribersMap.get(JSON.stringify(key)) || [];
    subscribersMap.set(JSON.stringify(key), [...subscribers, callback]);

    return () => {
      const subscribers = subscribersMap.get(JSON.stringify(key)) || [];
      subscribersMap.set(
        JSON.stringify(key),
        subscribers.filter((cb) => cb !== callback),
      );
    };
  }, [key, rerender]);

  const clear = useMemoizedFn(() => {
    setValue(null);
  });

  return {
    value,
    setValue,
    getValue,
    clear,
  };
}
